import { Box, Collapse } from '@ltvco/refresh-lib/theme';
import { useState } from 'react';

import {
  AccordionDetailList,
  Chevron,
  CompactReportFieldRow,
  ExpandSection,
  OwnerAccordionMain,
  OwnerAccordionName,
  OwnerAccordionRole,
  OwnerAccordionRow,
  OwnerAvatar,
  OwnerCardContent,
  PreviewCardSection,
  RecordItem,
  ShowMoreRelativesToggle,
  VStack,
} from '../../components';
import {
  formatLongDate,
  formatMonthYear,
  getInitials,
  titleCase,
} from '../utils';

import { AncestryLinkedSection } from './components';

import type {
  AncestryFact,
  AncestryReportData,
  PersonRelative,
} from 'components/DeepSearch/types';

const PREVIEW_COUNT = 5;

// ─── Life Events ─────────────────────────────────────────────────────────────

function formatFactDate(raw: string): string {
  if (!raw) {
    return '';
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return formatLongDate(raw);
  }
  if (/^\d{4}-\d{2}$/.test(raw)) {
    return formatMonthYear(raw);
  }
  return raw;
}

const FactItem = ({ fact }: { fact: AncestryFact }) => {
  const formattedDate = formatFactDate(fact.date);
  const shortValue = fact.value.length < 120 ? fact.value : '';
  const detail = [formattedDate, fact.place || shortValue]
    .filter(Boolean)
    .join(' · ');
  return (
    <RecordItem>
      <Box sx={{ fontSize: 16, fontWeight: 700 }}>{fact.type}</Box>
      {detail && (
        <Box sx={{ fontSize: 14, color: 'text.secondary', mt: '4px' }}>
          {detail}
        </Box>
      )}
    </RecordItem>
  );
};

export const LifeEventsSection = ({ data }: { data: AncestryReportData }) => {
  const facts = data.facts ?? [];

  if (facts.length === 0) {
    return null;
  }

  const preview = facts
    .slice(0, 3)
    .map((f) => f.type)
    .join(' · ');

  return (
    <PreviewCardSection title="Life Events">
      <AncestryLinkedSection>
        <ExpandSection
          label="Events"
          preview={preview || undefined}
          count={facts.length}
        >
          <VStack gap={8}>
            {facts.map((fact, i) => (
              <FactItem key={i} fact={fact} />
            ))}
          </VStack>
        </ExpandSection>
      </AncestryLinkedSection>
    </PreviewCardSection>
  );
};

// ─── Family & Relatives ──────────────────────────────────────────────────────

const RelativeAccordion = ({ relative }: { relative: PersonRelative }) => {
  const [open, setOpen] = useState(false);
  const role = relative.relationship || undefined;

  return (
    <Box>
      <OwnerAccordionRow
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <OwnerAvatar>{getInitials(relative.name)}</OwnerAvatar>
        <OwnerAccordionMain>
          <OwnerAccordionName>
            {titleCase(relative.name.toLowerCase())}
          </OwnerAccordionName>
          {role && <OwnerAccordionRole>{role}</OwnerAccordionRole>}
        </OwnerAccordionMain>
        <Chevron open={open} />
      </OwnerAccordionRow>
      <Collapse in={open}>
        <OwnerCardContent>
          <AccordionDetailList>
            {relative.address && (
              <CompactReportFieldRow
                label="Location"
                value={relative.address}
              />
            )}
            {relative.aliases.length > 0 && (
              <CompactReportFieldRow
                label="Also known as"
                value={relative.aliases
                  .slice(0, 3)
                  .map((a) => titleCase(a.toLowerCase()))
                  .join(', ')}
              />
            )}
          </AccordionDetailList>
        </OwnerCardContent>
      </Collapse>
    </Box>
  );
};

export const AncestryRelativesSection = ({
  data,
}: {
  data: AncestryReportData;
}) => {
  const relatives = data.relatives ?? [];

  if (relatives.length === 0) {
    return null;
  }

  const [expanded, setExpanded] = useState(false);
  const hasMore = relatives.length > PREVIEW_COUNT;
  const shown = expanded ? relatives : relatives.slice(0, PREVIEW_COUNT);

  return (
    <PreviewCardSection title="Family & Relatives">
      {shown.map((relative, i) => (
        <RelativeAccordion
          key={relative.id || `${relative.name}-${i}`}
          relative={relative}
        />
      ))}
      {hasMore && (
        <ShowMoreRelativesToggle
          expanded={expanded}
          hiddenCount={relatives.length - PREVIEW_COUNT}
          onToggle={() => setExpanded((v) => !v)}
        />
      )}
    </PreviewCardSection>
  );
};
